import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    CheckCircle2,
    Loader2,
    PiggyBank,
    Send,
    AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import OverrideReasonModal from "@/components/common/OverrideReasonModal";

const fmt = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

const TONE_CHIP = {
    green: "text-emerald-300 border-emerald-500/50 bg-emerald-900/30",
    yellow: "text-amber-200 border-amber-400/50 bg-amber-900/30",
    red: "text-rose-200 border-rose-400/50 bg-rose-900/40",
    unknown: "text-cream/60 border-cream/15 bg-cream/5",
};

const TONE_LABEL = {
    green: "Within range · Live",
    yellow: "Live · Soft nudge",
    red: "Live · Override on file",
    unknown: "Live",
};

function fmtDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

export default function PricingPanel() {
    const [rows, setRows] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [previews, setPreviews] = useState({}); // slug -> state (from /preview)
    const [busy, setBusy] = useState({});
    const [modal, setModal] = useState(null); // {slug, price, state} when confirming

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/partner/pricing");
            setRows(data.products);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load pricing");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const previewPrice = async (slug, price) => {
        if (!price || price <= 0) {
            setPreviews((p) => ({ ...p, [slug]: null }));
            return null;
        }
        try {
            const { data } = await api.post("/partner/pricing/preview", {
                slug,
                partner_price: price,
            });
            setPreviews((p) => ({ ...p, [slug]: data }));
            return data;
        } catch {
            return null;
        }
    };

    const apply = async (slug, price, overrideReason) => {
        setBusy((b) => ({ ...b, [slug]: true }));
        try {
            const payload = { slug, partner_price: price };
            if (overrideReason) payload.override_reason = overrideReason;
            const { data } = await api.post("/partner/pricing/submit", payload);
            const tone = data?.state?.tone || "green";
            if (tone === "green") {
                toast.success(`Live · ${fmt(data.applied_price)} is now on the storefront.`);
            } else if (tone === "yellow") {
                toast.success(
                    `Live · ${fmt(data.applied_price)}. ${data.state.message}`,
                    { duration: 6000 },
                );
            } else {
                toast.success(
                    `Live · ${fmt(data.applied_price)}. Override reason logged.`,
                    { duration: 6000 },
                );
            }
            setDrafts((d) => ({ ...d, [slug]: "" }));
            setPreviews((p) => ({ ...p, [slug]: null }));
            setModal(null);
            await load();
            return true;
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "override_reason_required") {
                toast.error(detail.message || "Reason required");
            } else {
                toast.error(typeof detail === "string" ? detail : "Submission failed");
            }
            return false;
        } finally {
            setBusy((b) => ({ ...b, [slug]: false }));
        }
    };

    const onSubmit = async (slug) => {
        const price = parseFloat(drafts[slug]);
        if (!price || price <= 0) {
            toast.error("Enter a valid price");
            return;
        }
        // Preview first so we know which band we're in.
        const state = (await previewPrice(slug, price))?.state;
        if (!state || state.tone !== "red") {
            // Green or yellow — direct apply.
            await apply(slug, price);
        } else {
            // Red — open modal, require reason.
            setModal({ slug, price, state });
        }
    };

    if (rows == null) {
        return (
            <div className="py-16 flex justify-center" data-testid="partner-pricing-loading">
                <Loader2 size={28} className="animate-spin text-gold" />
            </div>
        );
    }

    return (
        <section className="space-y-8" data-testid="partner-pricing-panel">
            <header>
                <span className="divider-rule text-gold">
                    <PiggyBank size={12} /> Pricing · Your Call
                </span>
                <h2 className="font-serif text-3xl md:text-4xl text-cream mt-4 leading-[1.05]">
                    Set your price —{" "}
                    <span className="italic text-gold">
                        we'll make sure it aligns with the market.
                    </span>
                </h2>
                <p className="mt-3 text-cream/60 text-sm leading-relaxed max-w-xl">
                    Market anchors are read-only reference. Set your price and
                    it updates live. We'll flag if it falls outside the market
                    range so you never undersell yourself — or overshoot.
                </p>
            </header>

            <div className="space-y-5">
                {rows.map((p) => {
                    const liveTone = p.state?.tone || "unknown";
                    const previewState = previews[p.slug]?.state;
                    const previewTone = previewState?.tone;
                    const nudgeTone = previewTone && previewTone !== "green" ? previewTone : null;
                    return (
                        <article
                            key={p.slug}
                            className="p-5 md:p-6 bg-cream/5 border border-cream/10 rounded-sm backdrop-blur"
                            data-testid={`pricing-row-${p.slug}`}
                        >
                            <div className="grid md:grid-cols-12 gap-5 items-start">
                                <div className="md:col-span-3 flex gap-3 items-start">
                                    <div className="relative w-16 h-16 shrink-0 rounded-sm overflow-hidden bg-cream/5">
                                        <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-serif text-lg text-cream leading-tight">{p.name}</h3>
                                        <p className="text-[0.6rem] tracking-[0.25em] uppercase text-cream/50 mt-1 truncate">
                                            {p.slug}
                                        </p>
                                    </div>
                                </div>

                                <div className="md:col-span-4 grid grid-cols-3 gap-3">
                                    {[
                                        { label: "Market Low", val: p.market_low },
                                        { label: "Suggested", val: p.suggested_price, hi: true },
                                        { label: "Market High", val: p.market_high },
                                    ].map((a) => (
                                        <div
                                            key={a.label}
                                            className={`text-center p-2.5 rounded-sm border ${
                                                a.hi ? "border-gold/40 bg-gold/5" : "border-cream/10 bg-cream/5"
                                            }`}
                                        >
                                            <p className="text-[0.55rem] tracking-[0.25em] uppercase text-cream/50">
                                                {a.label}
                                            </p>
                                            <p className={`font-serif text-base mt-1 ${a.hi ? "text-gold" : "text-cream"}`}>
                                                {fmt(a.val)}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <div className="md:col-span-2 text-center">
                                    <p className="text-[0.55rem] tracking-[0.25em] uppercase text-cream/50">Live Now</p>
                                    <p
                                        className="font-serif text-2xl text-emerald-300 mt-1"
                                        data-testid={`pricing-live-${p.slug}`}
                                    >
                                        {fmt(p.effective_price)}
                                    </p>
                                </div>

                                <div className="md:col-span-3">
                                    <label className="text-[0.55rem] tracking-[0.25em] uppercase text-cream/50 block">
                                        Your Price
                                    </label>
                                    <div className="mt-1.5 flex gap-2">
                                        <div className="relative flex-1">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/40 text-sm">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                disabled={busy[p.slug]}
                                                placeholder={String(p.suggested_price || "")}
                                                value={drafts[p.slug] ?? ""}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setDrafts((d) => ({ ...d, [p.slug]: v }));
                                                    const parsed = parseFloat(v);
                                                    if (parsed > 0) previewPrice(p.slug, parsed);
                                                    else setPreviews((pp) => ({ ...pp, [p.slug]: null }));
                                                }}
                                                data-testid={`pricing-input-${p.slug}`}
                                                className="w-full pl-7 pr-3 py-2 rounded-sm bg-cream/5 border border-cream/15 text-cream focus:outline-none focus:border-gold text-sm disabled:opacity-50"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => onSubmit(p.slug)}
                                            disabled={busy[p.slug] || !drafts[p.slug]}
                                            data-testid={`pricing-submit-${p.slug}`}
                                            className="btn-pill btn-gold shrink-0 text-[0.65rem] px-4 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {busy[p.slug] ? (
                                                <Loader2 size={13} className="animate-spin" />
                                            ) : (
                                                <>
                                                    <Send size={11} /> Set Live
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Soft inline nudge during typing (yellow) */}
                                    {nudgeTone === "yellow" && (
                                        <p
                                            className="mt-2 text-[0.65rem] text-amber-200 inline-flex items-center gap-1"
                                            data-testid={`pricing-nudge-${p.slug}`}
                                        >
                                            <AlertCircle size={10} />{" "}
                                            {previewState.message}
                                        </p>
                                    )}
                                    {/* Red inline warning — click will open modal */}
                                    {nudgeTone === "red" && (
                                        <p
                                            className="mt-2 text-[0.65rem] text-rose-300 inline-flex items-center gap-1"
                                            data-testid={`pricing-warn-${p.slug}`}
                                        >
                                            <AlertCircle size={10} />{" "}
                                            {previewState.message}{" "}
                                            <span className="opacity-70">
                                                — reason required on save.
                                            </span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 pt-3 border-t border-cream/10 flex items-center flex-wrap gap-3 text-xs">
                                <span
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[0.6rem] tracking-[0.2em] uppercase ${TONE_CHIP[liveTone] || TONE_CHIP.unknown}`}
                                    data-testid={`pricing-status-${p.slug}`}
                                >
                                    <CheckCircle2 size={10} /> {TONE_LABEL[liveTone] || TONE_LABEL.unknown}
                                </span>
                                {p.latest_override && p.latest_override.reason && (
                                    <span
                                        className="text-cream/60 italic"
                                        data-testid={`pricing-override-reason-${p.slug}`}
                                    >
                                        "{p.latest_override.reason}" · {fmtDate(p.latest_override.submitted_at)}
                                    </span>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>

            <OverrideReasonModal
                open={!!modal}
                title={
                    modal?.state?.direction === "below"
                        ? "Your price may be too low"
                        : "Your price may be too high"
                }
                subtitle={
                    modal
                        ? `${fmt(modal.price)} for ${modal.slug} — outside the ${fmt(rows.find((r) => r.slug === modal.slug)?.market_low)}–${fmt(rows.find((r) => r.slug === modal.slug)?.market_high)} market range.`
                        : ""
                }
                aiRationale={modal?.state?.message}
                tone="rose"
                placeholder="Help us understand your thinking — e.g. premium positioning, launch discount, loyalty tier…"
                confirmLabel="Save & Go Live"
                busy={!!(modal && busy[modal.slug])}
                onCancel={() => setModal(null)}
                onConfirm={(reason) => apply(modal.slug, modal.price, reason)}
                testId="pricing-override-modal"
            />
        </section>
    );
}
